/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-04-06 08:54:07
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';
import apiService from 'src/services/api-service';
import {
  ENABLED_MERCHANDISING_V2_RULES_REQUEST,
  ENABLED_MERCHANDISING_V2_RULES_SUCCESS,
  ENABLED_MERCHANDISING_V2_RULES_FAILURE,
  CREATE_MERCHANDISING_RULE_V2_FAILURE,
  CREATE_MERCHANDISING_RULE_V2_REQUEST,
  CREATE_MERCHANDISING_RULE_V2_SUCCESS,
  DELETE_MERCHANDISING_V2_RULE_FAILURE,
  DELETE_MERCHANDISING_V2_RULE_REQUEST,
  DELETE_MERCHANDISING_V2_RULE_SUCCESS,
  DUPLICATE_MERCHANDISING_V2_RULE_FAILURE,
  DUPLICATE_MERCHANDISING_V2_RULE_REQUEST,
  DUPLICATE_MERCHANDISING_V2_RULE_SUCCESS,
  GET_EXISTS_UNIVERSAL_RULE_FAILURE,
  GET_EXISTS_UNIVERSAL_RULE_REQUEST,
  GET_EXISTS_UNIVERSAL_RULE_SUCCESS,
  GET_MERCHANDISING_V2_RULE_FAILURE,
  GET_MERCHANDISING_V2_RULE_REQUEST,
  GET_MERCHANDISING_V2_RULE_SUCCESS,
  GET_MERCHANDISING_V2_RULES_FAILURE,
  GET_MERCHANDISING_V2_RULES_REQUEST,
  GET_MERCHANDISING_V2_RULES_SUCCESS,
  GET_PRODUCTS_FAILURE,
  GET_PRODUCTS_REQUEST,
  GET_PRODUCTS_SUCCESS,
  GET_PRODUCTS_WITH_FILTER_CONDITIONS_FAILURE,
  GET_PRODUCTS_WITH_FILTER_CONDITIONS_REQUEST,
  GET_PRODUCTS_WITH_FILTER_CONDITIONS_SUCCESS,
  PREVIEW_MERCHANDISING_V2_RULE_FAILURE,
  PREVIEW_MERCHANDISING_V2_RULE_REQUEST,
  PREVIEW_MERCHANDISING_V2_RULE_SUCCESS,
  RESET_MERCHANDISING_V2_RULE,
  UPDATE_MERCHANDISING_V2_RULE_FAILURE,
  UPDATE_MERCHANDISING_V2_RULE_REQUEST,
  UPDATE_MERCHANDISING_V2_RULE_SUCCESS,
  CHECK_DUPLICATE_TERM_FAILURE,
  CHECK_DUPLICATE_TERM_REQUEST,
  CHECK_DUPLICATE_TERM_SUCCESS
} from 'stores/flow-type/merchandising-v2.type';
import { trackFSEvent, trackFSEvent_CreateMerchandiseRule } from 'src/integrations/fullStory';

/**
 * call to enable/disabled merchandising rules v2
 * @return {object} The RSAA object for API middleware
 */
export const enableMerchandisingV2 = (enable: boolean, i18n) => {
  return apiService.sendRequest(
    'admin-v3.merchandising-v2.switch-merchandising-v2',
    { body: { enable } },
    [
      ENABLED_MERCHANDISING_V2_RULES_REQUEST,
      {
        type: ENABLED_MERCHANDISING_V2_RULES_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);
          return { data };
        }
      },
      {
        type: ENABLED_MERCHANDISING_V2_RULES_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.MerchandisingV2.Actions.duplicateMerchandising_Failure'),
            i18n
          );
          return errorResult;
        }
      }
    ]
  );
};

/**
 * call to get merchandising rules v2
 * @return {object} The RSAA object for API middleware
 */
export const getMerchandisingV2Rules = (
  q: string,
  skip: number,
  limit: number,
  ruleType: string = 'search',
  status: string,
  enabledFromDate: Date | 0,
  enabledToDate: Date | 0,
  i18n: I18n
) => {
  // Get effect

  const effectFrom = enabledFromDate ? Math.round(new Date(enabledFromDate) / 1000) : '';

  const effectTo = enabledToDate
    ? Math.round(new Date(enabledToDate) / 1000) + 60 * 60 * 24 - 1
    : '';

  return apiService.sendRequest(
    'admin-v3.merchandising-v2.get-rules',
    {
      queries: { q, skip, limit, ruleType, status, effectFrom, effectTo }
    },
    [
      GET_MERCHANDISING_V2_RULES_REQUEST,
      {
        type: GET_MERCHANDISING_V2_RULES_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);
          return { data };
        }
      },
      {
        type: GET_MERCHANDISING_V2_RULES_FAILURE,
        payload: async (action, state, res) =>
          await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.MerchandisingV2.Actions.getMerchandisingRules'),
            i18n
          )
      }
    ]
  );
};

/**
 * call to get merchandising rule
 * @return {object} The RSAA object for API middleware
 */
export const getMerchandisingV2Rule = (ruleId: string, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.merchandising-v2.get-rule', { params: { ruleId } }, [
    GET_MERCHANDISING_V2_RULE_REQUEST,
    {
      type: GET_MERCHANDISING_V2_RULE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { ...data };
      }
    },
    {
      type: GET_MERCHANDISING_V2_RULE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.MerchandisingV2.Actions.getMerchandising'),
          i18n
        )
    }
  ]);
};

/**
 * call to add new rule merchandising v2
 * @return {object} The RSAA object for API middleware
 */
export const addMerchandisingV2Rule = (merchandisingRule: Object, files = [], i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.merchandising-v2.add-rule',
    { body: merchandisingRule, attachements: files },
    [
      CREATE_MERCHANDISING_RULE_V2_REQUEST,
      {
        type: CREATE_MERCHANDISING_RULE_V2_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

        // Track Full Story event
        trackFSEvent_CreateMerchandiseRule(merchandisingRule);

          return { data, merchandisingRule };
        }
      },
      {
        type: CREATE_MERCHANDISING_RULE_V2_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.MerchandisingV2.Actions.createMerchandising_Failure'),
            i18n
          );

          return errorResult;
        }
      }
    ]
  );
};

/**
 * call to update merchandising V2 rule
 * @return {object} The RSAA object for API middleware
 */
export const updateMerchandisingV2Rule = (
  ruleId: string,
  merchandising: Object,
  files = [],
  actionName: string = '',
  i18n: I18n
) => {
  const params = { ruleId };
  return apiService.sendRequest(
    'admin-v3.merchandising-v2.update-rule',
    { params, body: merchandising, attachements: files },
    [
      UPDATE_MERCHANDISING_V2_RULE_REQUEST,
      {
        type: UPDATE_MERCHANDISING_V2_RULE_SUCCESS,
        payload: async (action, state, res) => {
          const data = await processRes(
            res,
            'info',
            actionName === 'changeStatus'
              ? i18n.translate(
                  'AdminHTML.MerchandisingV2.Actions.updateMerchandising_Success_ChangeStatus'
                )
              : i18n.translate(
                  'AdminHTML.MerchandisingV2.Actions.updateMerchandising_Success_NotChangeStatus'
                ),
            i18n
          );

          // Tracking event
          if (actionName === 'changeStatus') {
            const eventNameMapping = {
              enabled: 'Enable a Merchandise Rule',
              disabled: 'Disable a Merchandise Rule'
            };

            // Track Full Story event
            trackFSEvent(eventNameMapping[merchandising?.status], {
              feature: 'Merchandising V2',
              pageType: merchandising?.ruleType,
              selectedStrategies: merchandising?.strategies
            });
          } else {
            // Track Full Story event
            const dataTracking = {
              feature: 'Merchandising V2',
              pageType: merchandising?.ruleType,
              selectedStrategies: merchandising?.strategies
            };

            if (_.has(merchandising, 'isDisabledPerformanceRanking')) {
              _.set(
                dataTracking,
                'isDisabledPerformanceRanking',
                merchandising?.isDisabledPerformanceRanking
              );
            }
            trackFSEvent('Update a merchandising rule', dataTracking);
          }

          return { data, actionName };
        }
      },
      {
        type: UPDATE_MERCHANDISING_V2_RULE_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            actionName === 'changeStatus'
              ? i18n.translate(
                  'AdminHTML.MerchandisingV2.Actions.updateMerchandising_Failure_ChangeStatus'
                )
              : i18n.translate(
                  'AdminHTML.MerchandisingV2.Actions.updateMerchandising_Failure_NotChangeStatus'
                ),
            i18n
          );

          return errorResult;
        }
      }
    ]
  );
};

/**
 * call to clear data in merchandising V2 rule
 * @return {object} empty
 */
export const clearMerchandisingV2Rule = () => {
  return { type: UPDATE_MERCHANDISING_V2_RULE_SUCCESS, payload: { data: {} } };
};

/**
 * call to delete merchandising V2 rule
 * @return {object} The RSAA object for API middleware
 */
export const deleteMerchandisingV2Rule = (ruleId: string, i18n: I18n) => {
  const params = { ruleId };
  return apiService.sendRequest('admin-v3.merchandising-v2.delete-rule', { params }, [
    DELETE_MERCHANDISING_V2_RULE_REQUEST,
    {
      type: DELETE_MERCHANDISING_V2_RULE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await processRes(
          res,
          'info',
          i18n.translate('AdminHTML.MerchandisingV2.Actions.deleteMerchandising_Success'),
          i18n
        );

        // Track Full Story event
        trackFSEvent('Delete a Merchandise Rule', {
          feature: 'Merchandising V2',
          pageType: data?.rule?.ruleType,
          selectedStrategies: data?.rule?.strategies
        });

        return { data };
      }
    },
    {
      type: DELETE_MERCHANDISING_V2_RULE_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.MerchandisingV2.Actions.deleteMerchandising_Failure'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};

/**
 * call to duplicate merchandising V2 rule
 * @return {object} The RSAA object for API middleware
 */
export const duplicateMerchandisingV2Rule = (ruleId: string, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.merchandising-v2.get-rule', { params: { ruleId } }, [
    DUPLICATE_MERCHANDISING_V2_RULE_REQUEST,
    {
      type: DUPLICATE_MERCHANDISING_V2_RULE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        // Track Full Story event
        trackFSEvent('Duplicate a Merchandise Rule', {
          feature: 'Merchandising V2',
          pageType: data?.data?.ruleType,
          selectedStrategies: data?.data?.strategies
        });
        return { ...data };
      }
    },
    {
      type: DUPLICATE_MERCHANDISING_V2_RULE_FAILURE,
      payload: async (action, state, res) =>
        await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.MerchandisingV2.Actions.getMerchandising'),
          i18n
        )
    }
  ]);
};

/**
 * call to render preview merchandising V2 rule
 * @return {object} The RSAA object for API middleware
 */
export const previewMerchandisingV2Rule = (body: Object, i18n: I18n) => {
  return apiService.sendRequest('filter.merchandising-v2.preview', { body }, [
    PREVIEW_MERCHANDISING_V2_RULE_REQUEST,
    {
      type: PREVIEW_MERCHANDISING_V2_RULE_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { data, requestBody: body };
      }
    },
    {
      type: PREVIEW_MERCHANDISING_V2_RULE_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.MerchandisingV2.Actions.duplicateMerchandising_Failure'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};

/**
 * call to render preview merchandising V2 rule
 * @return {object} The RSAA object for API middleware
 */
export const getProductsWithFilterConditions = (body: Object, i18n: I18n) => {
  return apiService.sendRequest('filter.merchandising-v2.preview', { body }, [
    GET_PRODUCTS_WITH_FILTER_CONDITIONS_REQUEST,
    {
      type: GET_PRODUCTS_WITH_FILTER_CONDITIONS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return data;
      }
    },
    {
      type: GET_PRODUCTS_WITH_FILTER_CONDITIONS_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.MerchandisingV2.Actions.duplicateMerchandising_Failure'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};

/**
 * call to render preview merchandising V2 rule
 * @return {object} The RSAA object for API middleware
 */
export const getProducts = (queries: Object, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.merchandising-v2.get-products', { queries }, [
    GET_PRODUCTS_REQUEST,
    {
      type: GET_PRODUCTS_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return data;
      }
    },
    {
      type: GET_PRODUCTS_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.MerchandisingV2.Actions.duplicateMerchandising_Failure'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};

/**
 * call to reset merchandising V2 rule
 * @return {object} The RSAA object for API middleware
 */
export const resetRule = () => {
  return { type: RESET_MERCHANDISING_V2_RULE, payload: {} };
};

/**
 * call to open/close tour when switch new rule merchandising v2
 * @return {object} The boolean value
 */
export const isOpenNewVersion = (value: boolean) => {
  return { type: 'PRODUCT_TOUR_MERCHANDISING_V2', payload: value };
};

/**
 * call to set panelId/tabID to show in list rule merchandising v2
 * @return {object}
 */
export const setMerchandisingTab = (value: boolean) => {
  return { type: 'SET_TAB_RULES_MERCHANDISING_V2', payload: value };
};

export const checkExistsUniversalRule = (ruleType: string, i18n: I18n) => {
  return apiService.sendRequest(
    'admin-v3.merchandising-v2.check-universal-rule',
    { queries: { ruleType } },
    [
      GET_EXISTS_UNIVERSAL_RULE_REQUEST,
      {
        type: GET_EXISTS_UNIVERSAL_RULE_SUCCESS,
        payload: async (action, state, res) => {
          return await getJSON(res);
        }
      },
      {
        type: GET_EXISTS_UNIVERSAL_RULE_FAILURE,
        payload: async (action, state, res) => {
          return null;
        }
      }
    ]
  );
};

export const checkDuplicateTerm = (body, i18n: I18n) => {
  return apiService.sendRequest('admin-v3.merchandising-v2.check-exists-term', { body }, [
    CHECK_DUPLICATE_TERM_REQUEST,
    {
      type: CHECK_DUPLICATE_TERM_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);
        return { data, searchTerm: body.searchTerm };
      }
    },
    {
      type: CHECK_DUPLICATE_TERM_FAILURE,
      payload: async (action, state, res) => {
        const errorResult = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.VariantsDisplay.Actions.checkExistsMERProductId_Failure'),
          i18n
        );

        return errorResult;
      }
    }
  ]);
};
